import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "./ui/sheet";
import { Sparkles, Send, Loader2 } from "lucide-react";
import api from "../lib/api";
import { NAV } from "../lib/constants";

const PAGE_LABELS = Object.fromEntries(NAV.flatMap(g => g.items.map(i => [i.path, i.label])));

export default function AIAssistantDrawer({ open, onOpenChange }) {
  const location = useLocation();
  const pageLabel = PAGE_LABELS[location.pathname] || "Dashboard";
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hello — I'm your DPDP AI Copilot. I know which page you're on, so ask me anything about what you're looking at — or about the DPDP Act 2023, consent, breach timelines, cross-border transfers, and SDF obligations." },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);

  const send = async (preset) => {
    const q = (preset || input).trim();
    if (!q || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: q }]);
    setLoading(true);
    try {
      const context = `The user is currently on the "${pageLabel}" page (${location.pathname}) of their DPDP compliance platform. Interpret the question in the context of that module when relevant. Answer quickly and concisely (under 180 words) unless the user explicitly asks for detail.`;
      const { data } = await api.post("/ai/legal-qa", { question: q, context });
      setMessages((m) => [...m, { role: "assistant", content: data.answer }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: "Error contacting AI service. Please try again." }]);
    } finally { setLoading(false); }
  };

  const suggestions = [
    `What should I do on the ${pageLabel} page?`,
    `Which DPDP obligations does ${pageLabel} cover?`,
  ];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-lg p-0 flex flex-col surface" style={{ borderLeft: "1px solid var(--border)" }}>
        <SheetTitle className="sr-only">AI Copilot</SheetTitle>
        <SheetDescription className="sr-only">Context-aware DPDP assistant drawer</SheetDescription>
        <div className="p-5" style={{ borderBottom: "1px solid var(--border)" }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: "linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)" }}>
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="font-semibold text-base ink">AI Copilot</div>
              <div className="text-[11px] muted mono">Context-aware · DPDP knowledge base</div>
            </div>
          </div>
          <div className="mt-3 flex items-center gap-1.5 flex-wrap" data-testid="ai-page-context">
            <span className="text-[10px] font-semibold uppercase tracking-wider muted">Context:</span>
            <span className="text-[10.5px] font-semibold px-2 py-0.5 rounded-full" style={{ background: "var(--primary-soft)", color: "var(--primary)" }}>{pageLabel}</span>
          </div>
          <div className="mt-2 flex gap-1.5 flex-wrap">
            {suggestions.map((s, i) => (
              <button key={i} onClick={() => send(s)} data-testid={`ai-suggestion-${i}`}
                className="text-[10.5px] px-2 py-1 rounded-full transition-colors hover:text-white"
                style={{ border: "1px solid var(--border)", color: "var(--muted)" }}
                onMouseEnter={(e) => { e.currentTarget.style.background = "var(--primary)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}>
                {s}
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-app" data-testid="ai-messages">
          {messages.map((m, i) => (
            <div key={i} className={`text-sm rounded-2xl p-3.5 ${m.role === "user" ? "ml-8" : "mr-8"} card-shadow`}
                 style={{
                   background: m.role === "user" ? "var(--primary)" : "var(--surface)",
                   color: m.role === "user" ? "white" : "var(--ink)",
                   border: m.role === "user" ? "none" : "1px solid var(--border)",
                 }}>
              <div className={`text-[10px] font-semibold uppercase tracking-wider mb-1.5 ${m.role === "user" ? "opacity-80" : "muted"}`}>
                {m.role === "user" ? "You" : "Assistant"}
              </div>
              <div className="whitespace-pre-wrap leading-relaxed">{m.content}</div>
            </div>
          ))}
          {loading && (
            <div className="flex items-center gap-2 text-xs muted pl-2">
              <Loader2 className="w-3.5 h-3.5 animate-spin" style={{ color: "var(--primary)" }} /> Thinking...
            </div>
          )}
        </div>

        <div className="p-3 flex gap-2 surface" style={{ borderTop: "1px solid var(--border)" }}>
          <input
            data-testid="ai-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder={`Ask about ${pageLabel} or anything DPDP...`}
            className="flex-1 border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-transparent"
            style={{ borderColor: "var(--border)", background: "var(--bg)" }}
            onFocus={(e) => { e.currentTarget.style.borderColor = "var(--primary)"; e.currentTarget.style.boxShadow = "0 0 0 3px rgba(79,70,229,0.1)"; }}
            onBlur={(e) => { e.currentTarget.style.borderColor = "var(--border)"; e.currentTarget.style.boxShadow = "none"; }}
          />
          <button
            data-testid="ai-send"
            onClick={() => send()}
            disabled={loading}
            className="px-3.5 rounded-lg text-white disabled:opacity-50"
            style={{ background: "var(--primary)" }}
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
